import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import StatsStrip from "@/components/StatsStrip";
import BestFitStrip from "@/components/BestFitStrip";
import EditorialMarquee from "@/components/EditorialMarquee";
import Seo from "@/components/Seo";
import { INDUSTRIES } from "@/data/services";
import { FOUNDER } from "@/data/revamp";
import { organizationSchema, breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

const CHAPTERS = [
  {
    k: "01",
    title: "A boutique, not a bureau.",
    body: "Orphion is a deliberately small practice. Every engagement is scoped, staffed and executed with the same senior judgment — no juniorised handoffs, no template decks.",
  },
  {
    k: "02",
    title: "Built for the inflection.",
    body: "We do our best work in the seasons where structure matters most — first 100 hires, first international office, first PE round, first M&A. Moments where people decisions compound.",
  },
  {
    k: "03",
    title: "Global comp, local nuance.",
    body: "Salary and benefits work delivered across the US, UK, Canada, India, Poland, Spain and Ukraine — from statutory guardrails to competitive band architecture.",
  },
  {
    k: "04",
    title: "Delivered with trusted partners.",
    body: "Employer branding, engagement and creative work are delivered with vetted partners — so you get boutique-level craft with the horsepower of an agency network.",
  },
];

export default function AboutPage() {
  return (
    <div>
      <Seo
        title="About & Leadership — Orphion Global HR"
        description="A senior-led HR practice for Global Capability Centres and AI-native IT companies. Founder-led — 14+ years HR leadership across Miratech, Quantiphi and Capita, SHRM-SCP. Nivyan Business Solutions LLP, Bengaluru."
        path="/about"
        jsonLd={[
          organizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="pt-40 pb-24 bg-[var(--bg)] border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="overline mb-6">About · The practice</p>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE }}
              className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]"
            >
              A boutique HR practice for the{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                scaling
              </span>{" "}
              enterprise.
            </motion.h1>
          </div>
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.3, ease: EASE }}
              className="clip-frame aspect-[16/10]"
            >
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000"
                alt="Boardroom detail"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <p className="font-serif-accent italic text-white text-lg drop-shadow">
                  &ldquo;People decisions compound. So does every playbook.&rdquo;
                </p>
                <span className="text-[10px] uppercase tracking-[0.28em] text-white border border-white/50 px-2 py-1 bg-black/25 backdrop-blur-sm">
                  Nivyan LLP
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <StatsStrip />

      {/* Chapters */}
      <section className="py-24 md:py-32 bg-[var(--bg-2)] border-t border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="overline mb-8">Manifesto · In four chapters</p>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-16">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.k}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: 0.1 * i, ease: EASE }}
                className="border-t border-[var(--line-strong)] pt-8"
              >
                <div className="font-serif-accent italic text-[var(--accent)] text-3xl mb-6">
                  {c.k}
                </div>
                <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[var(--ink)] mb-6 leading-[1.05]">
                  {c.title}
                </h3>
                <p className="text-[var(--ink-80)] leading-relaxed">
                  {c.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee items={INDUSTRIES} speed={22} />

      {/* Leadership */}
      <section className="py-24 md:py-32 bg-[var(--bg)] border-t border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <p className="overline mb-6">Leadership</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] leading-[1.02] text-[var(--ink)]">
              Built by someone who has{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                held the seat
              </span>
              .
            </h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="md:col-span-8 border border-[var(--line-strong)] p-8 md:p-12"
          >
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-2">
              <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[var(--ink)]">
                {FOUNDER.name}
              </h3>
              <span className="text-xs uppercase tracking-[0.24em] text-[var(--ink-50)]">
                {FOUNDER.role}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 my-6">
              {FOUNDER.credentials.map((c) => (
                <span
                  key={c}
                  className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2.5 py-1"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="text-[var(--ink-80)] leading-relaxed max-w-2xl">{FOUNDER.bio}</p>
          </motion.div>
        </div>
      </section>

      <BestFitStrip />

      {/* CTA */}
      <section className="bg-[var(--bg)] py-24 border-t border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between gap-8 items-start md:items-end">
          <h2 className="font-display text-4xl md:text-6xl tracking-tight text-[var(--ink)] leading-[1.02] max-w-3xl">
            Ready to design a{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              people function
            </span>{" "}
            that scales with you?
          </h2>
          <Link to="/book-diagnostic" className="btn-pill">
            <span>Book a diagnostic call</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
